import React, { useState } from 'react';
import { useAuth } from '@/context/auth';
import { useLanguage } from '@/context/language-context';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';

// Réutiliser les mêmes options que BasicInfoTab
const genderOptions = [
  { value: 'female', label: 'gender.female' },
  { value: 'male', label: 'gender.male' },
  { value: 'transgender_female', label: 'gender.trans_female' },
  { value: 'transgender_male', label: 'gender.trans_male' },
  { value: 'non_binary', label: 'gender.non_binary' },
];

const orientationOptions = [
  { value: 'heterosexual', label: 'orientation.heterosexual' },
  { value: 'homosexual', label: 'orientation.homosexual' },
  { value: 'bisexual', label: 'orientation.bisexual' },
  { value: 'pansexual', label: 'orientation.pansexual' },
  { value: 'asexual', label: 'orientation.asexual' },
];

const ethnicityOptions = [
  { value: 'asian', label: 'ethnicity.asian' },
  { value: 'black', label: 'ethnicity.black' },
  { value: 'caucasian', label: 'ethnicity.caucasian' },
  { value: 'hispanic_latino', label: 'ethnicity.hispanic_latino' },
  { value: 'middle_eastern', label: 'ethnicity.middle_eastern' },
  { value: 'native_american', label: 'ethnicity.native_american' },
  { value: 'pacific_islander', label: 'ethnicity.pacific_islander' },
  { value: 'mixed', label: 'ethnicity.mixed' },
  { value: 'other', label: 'ethnicity.other' },
];

const bodyTypeOptions = [
  { value: 'slim', label: 'body_type.slim' },
  { value: 'athletic', label: 'body_type.athletic' },
  { value: 'average', label: 'body_type.average' },
  { value: 'curvy', label: 'body_type.curvy' },
  { value: 'bbw', label: 'body_type.bbw' },
  { value: 'muscular', label: 'body_type.muscular' },
  { value: 'petite', label: 'body_type.petite' },
];

interface CreateEscortProfileFormProps {
  userId: string;
  onProfileCreated: () => void; // Callback après création réussie
}

const CreateEscortProfileForm: React.FC<CreateEscortProfileFormProps> = ({ userId, onProfileCreated }) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    displayName: '',
    age: 18 as number | '',
    bioShort: '',
    bioFull: '',
    city: '',
    country: '',
    height: '' as number | '',
    weight: '' as number | '',
    gender: '',
    sexualOrientation: '',
    ethnicity: '',
    nationality: '',
    hairColor: '',
    eyeColor: '',
    bodyType: '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numberValue = value === '' ? '' : parseInt(value, 10);
    // Assurer que l'âge reste un nombre valide ou une chaîne vide pour la validation potentielle
    if (name === 'age' && (numberValue === '' || (Number.isInteger(numberValue) && numberValue >= 18 && numberValue <= 99))) {
       setFormData(prev => ({ ...prev, [name]: numberValue }));
    } else if (name !== 'age') {
        const isValidOtherNumber = numberValue === '' || (Number.isInteger(numberValue) && numberValue >= 0);
        if (isValidOtherNumber) {
            setFormData(prev => ({ ...prev, [name]: numberValue }));
        }
    }
  };

  const createProfile = async () => {
    setIsSaving(true);
    // Validation simple (pourrait être étendue avec Zod, etc.)
    if (!formData.displayName || !formData.city || !formData.country || !formData.gender) {
        toast({
            title: t('common.error'),
            description: t('escort:profile.creation_missing_fields'),
            variant: 'destructive',
        });
        setIsSaving(false);
        return;
    }

    try {
      const { error } = await supabase
        .from('escort_profiles')
        .insert({
          id: userId, // Lien vers l'utilisateur authentifié
          display_name: formData.displayName,
          age: formData.age === '' ? 18 : formData.age,
          bio_short: formData.bioShort,
          bio_full: formData.bioFull,
          city: formData.city,
          country: formData.country,
          // Convertir en nombre ou null si vide/invalide
          height: formData.height !== '' ? Number(formData.height) : null,
          weight: formData.weight !== '' ? Number(formData.weight) : null,
          gender: formData.gender,
          sexual_orientation: formData.sexualOrientation || null,
          ethnicity: formData.ethnicity || null,
          nationality: formData.nationality || null,
          hair_color: formData.hairColor || null,
          eye_color: formData.eyeColor || null,
          body_type: formData.bodyType || null,
          // Valeurs par défaut pour les autres champs non présents dans le formulaire initial
          is_verified: false,
          is_online: false,
          is_touring: false,
          // ... autres champs avec valeurs par défaut si nécessaire
        });

      if (error) throw error;

      toast({
        title: t('escort:profile.creation_success_title'),
        description: t('escort:profile.creation_success_desc'),
      });
      onProfileCreated(); // Appeler le callback pour rafraîchir les données dans le parent

    } catch (error) {
      console.error('Error creating escort profile:', error);
      toast({
        title: t('escort:profile.creation_error_title'),
        // Afficher un message d'erreur plus spécifique si possible
        // Utiliser instanceof Error pour accéder à message
        description: (error instanceof Error && error.message) || t('escort:profile.try_again_later'),
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Le JSX du formulaire est très similaire à BasicInfoTab,
  // juste adapté pour utiliser l'état local `formData` initialisé vide
  // et le bouton appelle `createProfile` au lieu de `saveProfileInfo`.
  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t('escort:profile.create_profile_title')}</h1>
        <p className="text-muted-foreground">{t('escort:profile.create_profile_desc')}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('escort:profile.personal_details')}</CardTitle>
           <CardDescription>{t('escort:profile.start_by_filling_info')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
         {/* Nom d'affichage */}
         <div className="space-y-2">
            <Label htmlFor="displayName">{t('escort:profile.display_name')} *</Label>
            <Input
                id="displayName"
                name="displayName"
                value={formData.displayName}
                onChange={handleInputChange}
                placeholder={t('escort:profile.display_name_placeholder')}
                required
            />
         </div>
          {/* Age */}
          <div className="space-y-2">
            <Label htmlFor="age">{t('escort:profile.age')} *</Label>
            <Input
                id="age"
                name="age"
                type="number"
                min={18}
                max={99}
                value={formData.age}
                onChange={handleNumberChange}
                required
            />
         </div>
         {/* Genre */}
         <div className="space-y-2">
            <Label htmlFor="gender">{t('escort:profile.gender')} *</Label>
            <Select
                name="gender"
                value={formData.gender}
                onValueChange={(value) => handleSelectChange('gender', value)}
                required
            >
                <SelectTrigger>
                  <SelectValue placeholder={t('escort:profile.select_gender')} />
                </SelectTrigger>
                <SelectContent>
                  {genderOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {t(option.label)}
                    </SelectItem>
                  ))}
                </SelectContent>
            </Select>
         </div>
         {/* Ville & Pays */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
                <Label htmlFor="city">{t('escort:profile.city')} *</Label>
                <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder={t('escort:profile.city_placeholder')}
                    required
                />
            </div>
            <div className="space-y-2">
                <Label htmlFor="country">{t('escort:profile.country')} *</Label>
                <Input // Pourrait être un Select si la liste des pays est limitée/connue
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    placeholder={t('escort:profile.country_placeholder')}
                    required
                />
            </div>
          </div>
          {/* Bio Courte */}
          <div className="space-y-2">
            <Label htmlFor="bioShort">{t('escort:profile.bio_short')}</Label>
            <Textarea
                id="bioShort"
                name="bioShort"
                value={formData.bioShort}
                onChange={handleInputChange}
                placeholder={t('escort:profile.bio_short_placeholder')}
                maxLength={150} // Exemple de limite
            />
            <p className="text-sm text-muted-foreground">
                {formData.bioShort.length} / 150
            </p>
          </div>
           {/* Bio Longue */}
          <div className="space-y-2">
            <Label htmlFor="bioFull">{t('escort:profile.bio_full')}</Label>
            <Textarea
                id="bioFull"
                name="bioFull"
                value={formData.bioFull}
                onChange={handleInputChange}
                placeholder={t('escort:profile.bio_full_placeholder')}
                rows={5}
            />
          </div>

           {/* Autres attributs (Optionnels pour la création initiale ?) */}
           <h3 className="text-lg font-medium pt-4">{t('escort:profile.physical_attributes')}</h3>
           <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               {/* Taille */}
               <div className="space-y-2">
                 <Label htmlFor="height">{t('escort:profile.height_cm')}</Label>
                 <Input id="height" name="height" type="number" value={formData.height} onChange={handleNumberChange} />
               </div>
               {/* Poids */}
               <div className="space-y-2">
                 <Label htmlFor="weight">{t('escort:profile.weight_kg')}</Label>
                 <Input id="weight" name="weight" type="number" value={formData.weight} onChange={handleNumberChange} />
               </div>
                {/* Couleur Cheveux */}
               <div className="space-y-2">
                 <Label htmlFor="hairColor">{t('escort:profile.hair_color')}</Label>
                 <Input id="hairColor" name="hairColor" value={formData.hairColor} onChange={handleInputChange} placeholder={t('escort:profile.hair_color_placeholder')}/>
               </div>
               {/* Couleur Yeux */}
               <div className="space-y-2">
                 <Label htmlFor="eyeColor">{t('escort:profile.eye_color')}</Label>
                 <Input id="eyeColor" name="eyeColor" value={formData.eyeColor} onChange={handleInputChange} placeholder={t('escort:profile.eye_color_placeholder')}/>
               </div>
                {/* Ethnicité */}
               <div className="space-y-2">
                 <Label htmlFor="ethnicity">{t('escort:profile.ethnicity')}</Label>
                 <Select name="ethnicity" value={formData.ethnicity} onValueChange={(v) => handleSelectChange('ethnicity', v)}>
                   <SelectTrigger><SelectValue placeholder={t('escort:profile.select_ethnicity')} /></SelectTrigger>
                   <SelectContent>{ethnicityOptions.map(o => <SelectItem key={o.value} value={o.value}>{t(o.label)}</SelectItem>)}</SelectContent>
                 </Select>
               </div>
               {/* Nationalité */}
               <div className="space-y-2">
                 <Label htmlFor="nationality">{t('escort:profile.nationality')}</Label>
                 <Input id="nationality" name="nationality" value={formData.nationality} onChange={handleInputChange} placeholder={t('escort:profile.nationality_placeholder')}/>
               </div>
               {/* Type de corps */}
                <div className="space-y-2">
                 <Label htmlFor="bodyType">{t('escort:profile.body_type')}</Label>
                 <Select name="bodyType" value={formData.bodyType} onValueChange={(v) => handleSelectChange('bodyType', v)}>
                   <SelectTrigger><SelectValue placeholder={t('escort:profile.select_body_type')} /></SelectTrigger>
                   <SelectContent>{bodyTypeOptions.map(o => <SelectItem key={o.value} value={o.value}>{t(o.label)}</SelectItem>)}</SelectContent>
                 </Select>
               </div>
               {/* Orientation Sexuelle */}
                <div className="space-y-2">
                 <Label htmlFor="sexualOrientation">{t('escort:profile.sexual_orientation')}</Label>
                 <Select name="sexualOrientation" value={formData.sexualOrientation} onValueChange={(v) => handleSelectChange('sexualOrientation', v)}>
                   <SelectTrigger><SelectValue placeholder={t('escort:profile.select_orientation')} /></SelectTrigger>
                   <SelectContent>{orientationOptions.map(o => <SelectItem key={o.value} value={o.value}>{t(o.label)}</SelectItem>)}</SelectContent>
                 </Select>
               </div>
           </div>

        </CardContent>
        <CardFooter>
          <Button onClick={createProfile} disabled={isSaving}>
            {isSaving ? t('common:saving') : t('escort:profile.create_profile_button')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default CreateEscortProfileForm; 